import classnames from 'classnames'
import React, { ForwardedRef, SyntheticEvent, useImperativeHandle, useMemo, useRef, useState } from 'react'

import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import ImageDomain from '@src/quince-site-common/src/domains/Image'
import PartnerProduct from '@src/quince-site-common/src/domains/PartnerProduct'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import partnerModalStateManager from '@src/quince-site-common/src/redux/globalStateManager/PartnerModalStateManager'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackClickEvent } from '@src/telemetry/track'

import { Icon, Image, ImageRefType, Link } from '../../atoms'
import RoundAddButton from '../productCard/icons/roundAddButton.inline.svg'

import { getPartnerProductLink } from './partnerProductCard.helpers'
import styles from './partnerProductCard.module.scss'

export interface PartnerProductCardProps {
  product: PartnerProduct
  className?: string
  titleClassName?: string
  priceClassName?: string
  productPrefixPriceClassName?: string
  imageClassName?: string
  wishlistIconClassName?: string
  onProductClick?: () => void
  onProductCardClick?: () => void
  imageRef?: ImageRefType
  eagerLoadImage?: boolean
  selectorKey?: string
  tracker?: string
  popupTracker?: string
}

export const PartnerProductCard = React.forwardRef(
  ({ className = '', ...props }: PartnerProductCardProps, ref: ForwardedRef<HTMLDivElement | null>) => {
    const { product } = props
    const { deviceType } = useDeviceInfo()
    const productCard = useRef<HTMLDivElement | null>(null)
    const [isHovered, setHovered] = useState(false)

    useImperativeHandle(ref, () => productCard.current)

    const isMobile = deviceType === DEVICE_TYPES.MOBILE
    const partnerName = props.product.title
    const productImageProps = useMemo(
      () =>
        ImageDomain.fromContentful(
          product.partnerProductImages[!isMobile && isHovered && product.partnerProductImages.length > 1 ? 1 : 0],
        ),
      [product.partnerProductImages, isMobile, isHovered],
    )

    const openParterProductPopup = (event: SyntheticEvent) => {
      event.preventDefault()
      event.stopPropagation()
      trackClickEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
        trackGA: true,
        trackInternal: true,
        action: props.tracker ? `${props.tracker}__click_partner_quick_shop` : 'click_partner_quick_shop',
        context: {
          title: product.title,
        },
      })
      if (props.popupTracker) {
        partnerModalStateManager.setPopupTracker(props.popupTracker)
      }
      partnerModalStateManager.setPartnerProduct(product)
      partnerModalStateManager.setPartnerPDPModal(true)
    }

    return (
      <div ref={productCard} className={classnames(styles.wrapper, className)}>
        <div
          onMouseEnter={() => {
            setHovered(true)
          }}
          onMouseLeave={() => {
            setHovered(false)
          }}
          className={classnames(styles.imageWrapper, props.imageClassName)}
        >
          <div className={styles.topSectionWrapper}>
            {productImageProps.url && (
              <Image
                alt={'product-image'}
                ref={props.imageRef}
                className={classnames(styles.productImage)}
                {...productImageProps}
                loading={props.eagerLoadImage ? 'eager' : 'lazy'}
              />
            )}
            <div className={styles.buttonsWrapper}>
              {!isMobile && (
                <Icon className={styles.plusIcon} onClick={openParterProductPopup} iconSvg={RoundAddButton} />
              )}
            </div>
          </div>
        </div>
        <div className={styles.bottomSectionWrapper}>
          <div className={styles.nameAndPriceSection}>
            <div className={styles.productNameWrapper}>
              <h4 className={classnames(styles.productPrefixPrice)}>
                <Link
                  selectorKey={props.selectorKey}
                  className={styles.partherProductCardLink}
                  href={getPartnerProductLink(product, props.tracker) || product.slug}
                  enableEarlyFetch={false}
                  onClick={() => {
                    trackClickEvent(TELEMETRY_EVENT_NAMES.PRODUCT_CLICK, {
                      trackInternal: true,
                      action: 'click_partner_product_card',
                      label: props.tracker ? `${props.tracker}__click_product_carousel` : undefined,
                      value: product.slug,
                      context: {
                        title: product.title,
                        contentContainerType: CUSTOM_SECTION_CONTAINER_TYPES.PARTNER_PRODUCT_CARD,
                        contentContainerName: 'Partner Products List',
                        contentContainerDisplayName: 'Partner Products List',
                      },
                    })
                  }}
                >
                  {partnerName}
                </Link>
              </h4>
            </div>
            <div className={styles.productPriceWrapper}>
              <div className={classnames(styles.productPrice, props.priceClassName)}>{product.subtitle}</div>
            </div>
          </div>
        </div>
      </div>
    )
  },
)

PartnerProductCard.displayName = 'PartnerProductCard'

export default PartnerProductCard
